import React from 'react';

import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import {
  useActivateDialog,
  useConfirmDialog,
} from '../../../../hooks/useConfirmDialog';
import { useOnSaved } from '../../../../hooks/useOnSaved';

import {
  Client,
  ClientAddress,
  ClientAdmin,
  ClientContact,
} from '../../../../data/models/client.model';
import * as clientSlice from '../../../../data/client.slice';

import ClientProfile from '../ClientProfile';
import ClientContacts from '../ClientContacts';
import ClientAdmins from '../ClientAdmins';
import ClientAddresses from '../ClientAddresses';
import ClientProfileDialog from '../ClientProfileDialog';
import AddressDialog from '../AddressDialog';
import ContactDialog from '../ContactDialog';
import AdminDialog from '../AdminDialog';
import ConfirmationDialog from '../../../ConfirmationDialog';
import ActivateDialog from '../../ActivateDialog';

interface ClientProfileTabProps {
  client?: Client;
  readonly: boolean;
  userRoleIDs: number[];
  userCategoryTypeIDs: number[];
  onAdminPageChange: (page: number) => void;
}

export default function ClientProfileTab({
  client,
  readonly,
  userRoleIDs,
  userCategoryTypeIDs,
  onAdminPageChange,
}: ClientProfileTabProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const addresses = useAppSelector(clientSlice.selectAddresses);
  const contacts = useAppSelector(clientSlice.selectContacts);
  const admins = useAppSelector(clientSlice.selectAdmins);
  const savedClient = useAppSelector(clientSlice.selectSavedClient);

  const [clientProfileDialogOpen, setClientProfileDialogOpen] =
    React.useState(false);
  const [editingAddress, setEditingAddress] = React.useState<ClientAddress>();
  const [editingContact, setEditingContact] = React.useState<ClientContact>();
  const [addressDialogOpen, setAddressDialogOpen] = React.useState(false);
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = React.useState(false);
  const confirmDialog = useConfirmDialog();
  const activateDialog = useActivateDialog();

  // The profile dialog can't close itself after a save: saving clears the
  // current client, which unmounts the dialog until the saved client arrives.
  useOnSaved(savedClient, () => setClientProfileDialogOpen(false));

  const editProfile = () => {
    setClientProfileDialogOpen(true);
  };

  const updateClientEffectiveDates = (
    bc: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(clientSlice.updateClientEffectiveDatesAsync(bc, start, end));
  };

  const activateClient = (client: Client) => {
    activateDialog.request({
      title: t('client.confirmActivateClientTitle'),
      message: t('client.confirmActivateClientMessage'),
      forActivate: true,
      onConfirm: (start, end) =>
        updateClientEffectiveDates(client.clientBC, start, end),
    });
  };

  const deactivateClient = (client: Client) => {
    activateDialog.request({
      title: t('client.confirmDeactivateClientTitle'),
      message: t('client.confirmDeactivateClientMessage'),
      forActivate: false,
      onConfirm: (start, end) =>
        updateClientEffectiveDates(client.clientBC, start, end),
    });
  };

  const addAddress = () => {
    setEditingAddress(undefined);
    setAddressDialogOpen(true);
  };

  const editAddress = (address: ClientAddress) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const activateAddress = (address: ClientAddress) => {
    confirmDialog.request({
      title: t('client.confirmActivateAddressTitle'),
      message: t('client.confirmActivateAddressMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.activateClientAddressAsync(client!.clientBC, address),
        ),
    });
  };

  const deactivateAddress = (address: ClientAddress) => {
    confirmDialog.request({
      title: t('client.confirmDeactivateAddressTitle'),
      message: t('client.confirmDeactivateAddressMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deactivateClientAddressAsync(client!.clientBC, address),
        ),
    });
  };

  const deleteAddress = (address: ClientAddress) => {
    confirmDialog.request({
      title: t('client.confirmDeleteAddressTitle'),
      message: t('client.confirmDeleteAddressMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientAddressAsync(client!.clientBC, address),
        ),
    });
  };

  const addContact = () => {
    setEditingContact(undefined);
    setContactDialogOpen(true);
  };

  const editContact = (contact: ClientContact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const activateContact = (contact: ClientContact) => {
    confirmDialog.request({
      title: t('client.confirmActivateContactTitle'),
      message: t('client.confirmActivateContactMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.activateClientContactAsync(client!.clientBC, contact),
        ),
    });
  };

  const deactivateContact = (contact: ClientContact) => {
    confirmDialog.request({
      title: t('client.confirmDeactivateContactTitle'),
      message: t('client.confirmDeactivateContactMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deactivateClientContactAsync(client!.clientBC, contact),
        ),
    });
  };

  const deleteContact = (contact: ClientContact) => {
    confirmDialog.request({
      title: t('client.confirmDeleteContactTitle'),
      message: t('client.confirmDeleteContactMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientContactAsync(client!.clientBC, contact),
        ),
    });
  };

  const addAdmin = () => {
    setAdminDialogOpen(true);
  };

  const resendInvite = (admin: ClientAdmin) => {
    dispatch(
      clientSlice.resendClientAdminInviteAsync(
        client!.clientBC,
        admin.userAccountID!,
      ),
    );
  };

  const deleteAdmin = (admin: ClientAdmin) => {
    confirmDialog.request({
      title: t('client.confirmDeleteAdminTitle'),
      message: t('client.confirmDeleteAdminMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientAdminAsync(
            client!.clientBC,
            admin.userAccountID!,
          ),
        ),
    });
  };

  return (
    <>
      <ClientProfile
        client={client}
        edit={editProfile}
        activate={activateClient}
        deactivate={deactivateClient}
        readonly={readonly}
      />
      <ClientAddresses
        clientAddresses={client ? addresses : undefined}
        addAddress={addAddress}
        editAddress={editAddress}
        activateAddress={activateAddress}
        deactivateAddress={deactivateAddress}
        deleteAddress={deleteAddress}
        readonly={readonly}
      />
      <ClientContacts
        clientContacts={client ? contacts : undefined}
        addContact={addContact}
        editContact={editContact}
        activateContact={activateContact}
        deactivateContact={deactivateContact}
        deleteContact={deleteContact}
        readonly={readonly}
      />
      <ClientAdmins
        clientAdmins={admins}
        changePage={onAdminPageChange}
        addAdmin={addAdmin}
        resendInvite={resendInvite}
        deleteAdmin={deleteAdmin}
        readonly={readonly}
      />
      {client && (
        <>
          {/* Form dialogs mount on open so their forms and lookup fetches
              don't run on every page load. */}
          {clientProfileDialogOpen && (
            <ClientProfileDialog
              title={t('client.editProfile')}
              client={client}
              open={clientProfileDialogOpen}
              close={() => setClientProfileDialogOpen(false)}
            />
          )}
          {addressDialogOpen && (
            <AddressDialog
              title={
                editingAddress
                  ? t('client.editAddress')
                  : t('client.addAddress')
              }
              open={addressDialogOpen}
              client={client}
              address={editingAddress}
              close={() => setAddressDialogOpen(false)}
            />
          )}
          {contactDialogOpen && (
            <ContactDialog
              title={
                editingContact
                  ? t('client.editContact')
                  : t('client.addContact')
              }
              open={contactDialogOpen}
              client={client}
              contact={editingContact}
              close={() => setContactDialogOpen(false)}
            />
          )}
          {adminDialogOpen && (
            <AdminDialog
              title={t('client.addAdmin')}
              open={adminDialogOpen}
              client={client}
              close={() => setAdminDialogOpen(false)}
              userRoleIDs={userRoleIDs}
              userCategoryTypeIDs={userCategoryTypeIDs}
            />
          )}
          <ConfirmationDialog {...confirmDialog.dialogProps} />
          <ActivateDialog {...activateDialog.activateDialogProps} />
        </>
      )}
    </>
  );
}
